import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, PlayCircle } from "lucide-react";
import { OrbitWordmark } from "@/components/Brand";
import { OrbitMap } from "@/components/OrbitMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";

const PREVIEW = [
  { key: "customers", label: "Customers", score: 55, status: "Developing" },
  { key: "sales", label: "Sales", score: 40, status: "At Risk" },
  { key: "operations", label: "Operations", score: 58, status: "Developing" },
  { key: "finance", label: "Finance", score: 66, status: "Strong" },
  { key: "technology", label: "Technology", score: 45, status: "At Risk" },
  { key: "ai", label: "AI", score: 30, status: "Opportunity" },
];

export function AuthBrandPanel({ children }) {
  return (
    <div className="relative hidden overflow-hidden bg-primary text-primary-foreground lg:flex lg:flex-col lg:justify-between lg:p-12">
      <div className="pointer-events-none absolute -left-24 bottom-[-6rem] h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
      <Link to="/"><OrbitWordmark className="[&_*]:text-primary-foreground" subtle /></Link>
      <div className="relative">
        <div className="rounded-2xl border border-primary-foreground/10 bg-primary-foreground/5 p-6 backdrop-blur">
          <OrbitMap domains={PREVIEW} size={360} />
        </div>
        {children}
      </div>
      <p className="relative text-sm text-primary-foreground/60">
        "Serious technology. Serious thinking. Practical outcomes." — MYTHIVA™ by Mythos India Studios
      </p>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login, demoLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/app");
    else setError(res.error);
  };

  const startDemo = async () => {
    setDemoLoading(true);
    const res = await demoLogin();
    setDemoLoading(false);
    if (res.ok) {
      toast.success("Demonstration company loaded");
      navigate("/app");
    } else toast.error("Couldn't start the demo", { description: res.error });
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <AuthBrandPanel>
        <p className="relative mt-6 max-w-sm text-lg font-medium leading-relaxed">
          Welcome back. Continue mapping where your business should transform next.
        </p>
      </AuthBrandPanel>

      <div className="flex flex-col justify-center px-6 py-12 sm:px-12">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 lg:hidden"><OrbitWordmark /></div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Sign in to ORBIT</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Access your transformation workspace.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password" />
            </div>
            {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="h-11 w-full" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in…" : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" className="h-11 w-full" onClick={startDemo} disabled={demoLoading} data-testid="login-demo-btn">
            <PlayCircle className="h-4 w-4" /> {demoLoading ? "Loading…" : "Explore the Demo (Aarohan)"}
          </Button>

          <p className="mt-8 text-center text-sm text-muted-foreground">
            New to ORBIT?{" "}
            <Link to="/register" className="font-semibold text-accent hover:underline" data-testid="to-register-link">Start your assessment</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
